import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { CardSkeleton, InlineSkeleton } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { AttendanceDaysTable } from '@/modules/time-attendance/attendance/components/attendance-days-table/attendance-days-table';
import { useEmployeeWorkStatusEligibility } from '@/modules/hr-directory/hooks/use-work-status-eligibility';
import { useGetOvertimeBalance, UserOvertimePanel } from '@/modules/time-attendance/overtime';
import { useDepartmentScope, useEmployeesByUserIds } from '@/modules/shared/hr-lookups';
import { AttendanceMonthlyGrid } from '../../components/attendance-monthly-grid/attendance-monthly-grid';
import { useGetTargetScope } from '../../hooks/use-manage-attendance';
import {
  APP_PATHS,
  EMPLOYEE_ATTENDANCE_TABS,
  EmployeeAttendanceTab,
  TEAM_ATTENDANCE_TABS,
} from '@/constant/app-paths';
import { useUrlTab } from '@/hooks/use-linked-record';

const { LIST, CALENDAR, OVERTIME } = EMPLOYEE_ATTENDANCE_TABS;

export const UserAttendanceHistoryPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId = '' } = useParams<{ userId: string }>();
  const { canViewAll, departments, isLoading: isDepartmentScopeLoading } = useDepartmentScope();

  // A reviewer limited to some departments could otherwise reach any employee's history by typing
  // the URL directly, bypassing the list page's department filter — so re-check here that the
  // employee's department is one they may see. `VIEW_ALL_DEPARTMENTS` skips the check.
  const { data: targetScope, isLoading: isTargetScopeLoading } = useGetTargetScope(
    canViewAll ? undefined : userId
  );
  const isScopeLoading = !canViewAll && (isDepartmentScopeLoading || isTargetScopeLoading);
  const isOutOfScope =
    !canViewAll &&
    !isScopeLoading &&
    Boolean(targetScope) &&
    !departments.some((department) => department.ItemId === targetScope?.DepartmentId);

  // The employee's overtime, like the Overtime tab of My Attendance: for reviewers who may view
  // overtime, and only when overtime is on for the employee (they have a balance row). The Overtime
  // data rules still limit which rows come back.
  const canViewOvertime = useHasPermission(HRM_PERMISSIONS.TIME_ATTENDANCE.OVERTIME_VIEW);
  const { data: overtimeBalance, isLoading: isOvertimeLoading } = useGetOvertimeBalance(
    canViewOvertime ? userId : ''
  );
  const hasOvertime = canViewOvertime && Boolean(overtimeBalance);
  const isOvertimeTabPending = canViewOvertime && isOvertimeLoading;

  // List and Calendar need everyone's attendance; a reviewer who may only see overtime (opened from the
  // Overtime tab of Team Attendance) gets just the Overtime tab. The tab lives in the URL (`?tab=`).
  const canViewAttendance = useHasPermission(HRM_PERMISSIONS.TIME_ATTENDANCE.ATTENDANCE_VIEW_ALL);
  const tabs: EmployeeAttendanceTab[] = [
    ...(canViewAttendance ? [LIST, CALENDAR] : []),
    // While overtime is being checked, keep a linked `?tab=overtime` instead of falling back.
    ...(hasOvertime || isOvertimeTabPending ? [OVERTIME] : []),
  ];
  const [tab, setTab] = useUrlTab(tabs, tabs[0] ?? LIST);
  const backPath = canViewAttendance
    ? APP_PATHS.TEAM_ATTENDANCE
    : `${APP_PATHS.TEAM_ATTENDANCE}?tab=${TEAM_ATTENDANCE_TABS.OVERTIME}`;

  const { byUserId, isLoading: isEmployeeLoading } = useEmployeesByUserIds([userId]);
  const employee = byUserId.get(userId);
  const employeeLine = [
    employee?.DisplayName ?? overtimeBalance?.ResourceName,
    employee?.Email,
    employee?.DepartmentName ?? overtimeBalance?.DepartmentName,
  ]
    .filter(Boolean)
    .join(' · ');

  // The Overtime and Break time columns follow the employee's own permissions (looked up in IAM),
  // not the reviewer's; they stay hidden while those aren't known.
  const eligibility = useEmployeeWorkStatusEligibility(userId || undefined);

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0"
          aria-label={t('BACK')}
          onClick={() => navigate(backPath)}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex min-w-0 flex-col">
          <h1 className="truncate text-lg font-semibold">{t('ATTENDANCE_HISTORY')}</h1>
          {!isOutOfScope && isEmployeeLoading && !employeeLine && (
            <InlineSkeleton className="mt-1 w-64 max-w-full" />
          )}
          {!isOutOfScope && employeeLine && (
            <span className="break-words text-sm text-muted-foreground">{employeeLine}</span>
          )}
        </div>
      </div>

      {isOutOfScope ? (
        <p className="text-sm text-muted-foreground">{t('ATTENDANCE_OUT_OF_DEPARTMENT_SCOPE')}</p>
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          {/* Full width on a phone, each tab an equal share (scrolls if they don't fit). */}
          <TabsList className="max-sm:w-full max-sm:justify-start max-sm:overflow-x-auto">
            {canViewAttendance && (
              <>
                <TabsTrigger value={LIST} className="max-sm:flex-1">
                  {t('LIST_VIEW')}
                </TabsTrigger>
                <TabsTrigger value={CALENDAR} className="max-sm:flex-1">
                  {t('CALENDAR_VIEW')}
                </TabsTrigger>
              </>
            )}
            {hasOvertime && (
              <TabsTrigger value={OVERTIME} className="max-sm:flex-1">
                {t('OVERTIME_HOURS')}
              </TabsTrigger>
            )}
            {/* Whether the employee has overtime is still being checked: hold the tab's place. */}
            {isOvertimeTabPending && <Skeleton className="mx-1 h-6 w-28 max-sm:flex-1" />}
          </TabsList>
          {canViewAttendance && (
            <>
              <TabsContent value={LIST}>
                {/* Every day of the range, like the employee's own list; each day's correction shows
                    as its status (none is requested from here). */}
                {isScopeLoading ? (
                  <CardSkeleton lines={8} />
                ) : (
                  <AttendanceDaysTable
                    userId={userId}
                    showBreak={eligibility?.breaks === true}
                    showOvertime={eligibility?.overtime === true}
                    showCorrectionStatus
                  />
                )}
              </TabsContent>
              <TabsContent value={CALENDAR}>
                {isScopeLoading ? (
                  <CardSkeleton bodyClassName="h-96 w-full" />
                ) : (
                  <AttendanceMonthlyGrid userId={userId} />
                )}
              </TabsContent>
            </>
          )}
          {/* While overtime is still being checked, a linked Overtime tab shows its loading state. */}
          {isOvertimeTabPending && tab === OVERTIME && <CardSkeleton lines={6} />}
          {hasOvertime && (
            <TabsContent value={OVERTIME}>
              <UserOvertimePanel userId={userId} />
            </TabsContent>
          )}
        </Tabs>
      )}
    </div>
  );
};
